/**
 * SUNTRONIX ONE — ONE CONNECT · Free & Open connectors.
 *
 * Renders the approved Free-First integration catalogue with true cost
 * class labels (§39) — never blanket "Free".  Every row shows source,
 * freshness, cache state and errors per PRD §30 and §31.
 */
import { useEffect, useState, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import {
  Loader2, RefreshCcw, ShieldCheck, Cable, AlertTriangle,
  Globe2, MapPin, CalendarClock, Landmark, Package, TrendingUp,
} from "lucide-react";

const COST_LABEL = {
  FREE_PUBLIC_API:            { label: "Free public API",     tone: "mint"  },
  FREE_PUBLIC_DATA:           { label: "Free public data",    tone: "mint"  },
  OPEN_GOVERNMENT_DATA:       { label: "Open government data", tone: "blue" },
  OPEN_SOURCE_SELF_HOSTED:    { label: "Open-source · self-hosted", tone: "blue"  },
  FREE_TIER:                   { label: "Free tier",             tone: "amber" },
  CUSTOMER_LICENCE:           { label: "Customer licence req.", tone: "coral" },
};
const STATE_TONE = {
  HEALTHY:    { bg: "#ECFDF6", fg: "#169C70", label: "Healthy"   },
  RUNNING:    { bg: "#EFF5FF", fg: "#246BFD", label: "Syncing"   },
  DEGRADED:   { bg: "#FFF0F2", fg: "#C2333F", label: "Degraded" },
  NEVER_RUN:  { bg: "#F2F4F7", fg: "#667085", label: "Never run" },
};
const CATEGORY_ICON = {
  SECURITY_INTELLIGENCE: ShieldCheck,
  GLOBAL_REFERENCE:       Globe2,
  INDIA_REFERENCE:         MapPin,
  CALENDAR_AND_SLA:       CalendarClock,
  OBSERVABILITY:            TrendingUp,
  FILE_SECURITY:            ShieldCheck,
  PLATFORM_SECURITY:       ShieldCheck,
  API_QUALITY:              Package,
};

export default function OneConnectFree() {
  const [catalogue, setCatalogue] = useState(null);
  const [health, setHealth] = useState([]);
  const [syncing, setSyncing] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const [c, h] = await Promise.all([
        api.get("/one-connect/free/catalogue"),
        api.get("/one-connect/free/health"),
      ]);
      setCatalogue(c.data);
      setHealth(h.data.items || []);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const runSync = async (id) => {
    setSyncing(id);
    try {
      const { data } = await api.post(`/one-connect/free/${id}/sync`);
      toast.success(`Synced · ${data.records ?? data.records_in_window ?? "—"} records`);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSyncing(null); }
  };

  const healthBy = health.reduce((acc, h) => (acc[h.connector_id] = h, acc), {});
  const byCategory = (catalogue?.connectors || []).reduce((acc, c) => {
    (acc[c.category] = acc[c.category] || []).push(c); return acc;
  }, {});

  return (
    <div className="max-w-[1400px] space-y-4" data-testid="oc-free-page">
      <div>
        <div className="text-[10px] font-semibold tracking-[0.08em] uppercase text-[#667085]">
          Administration · Integrations · ONE CONNECT
        </div>
        <h1 className="text-2xl font-heading font-semibold text-[#101828] tracking-tight">
          Free &amp; open connectors
        </h1>
        <p className="text-[13px] text-[#667085] mt-1 max-w-3xl">
          High-quality free public APIs, open government data and open-source
          tooling — every one gated by cache, rate limits, source attribution
          and freshness so <b>free never means uncontrolled</b>.
        </p>
      </div>

      {/* Health strip */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {(catalogue?.connectors || []).slice(0, 4).map((c) => {
          const h = healthBy[c.id] || {};
          const tone = STATE_TONE[h.state] || STATE_TONE.NEVER_RUN;
          return (
            <div key={c.id} className="rounded border border-slate-200 bg-white p-3"
                    data-testid={`oc-free-strip-${c.id}`}>
              <div className="flex items-center justify-between">
                <div className="text-[11px] font-medium text-[#101828]">{c.name}</div>
                <span className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold"
                        style={{ background: tone.bg, color: tone.fg }}>
                  {tone.label}
                </span>
              </div>
              <div className="mt-1 text-2xl font-heading font-semibold text-[#101828] tabular-nums">
                {h.record_count?.toLocaleString() || 0}
              </div>
              <div className="text-[10px] text-[#667085] mt-0.5">
                {h.last_success_at
                  ? `Last sync ${new Date(h.last_success_at).toLocaleString()}`
                  : "Never synced"}
              </div>
            </div>
          );
        })}
      </div>

      {/* Catalogue grouped by category */}
      {Object.keys(byCategory).map((cat) => {
        const Icon = CATEGORY_ICON[cat] || Cable;
        return (
          <div key={cat} data-testid={`oc-free-cat-${cat}`}>
            <div className="text-[11px] uppercase tracking-[0.14em] text-[#667085] mt-4 mb-2 flex items-center gap-2">
              <Icon className="h-3 w-3" strokeWidth={1.6} />
              {catalogue?.category_labels?.[cat] || cat}
            </div>
            <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-[10px] uppercase text-[#667085]">
                  <tr>
                    <th className="text-left px-3 py-2">Provider</th>
                    <th className="text-left px-3 py-2">Cost class</th>
                    <th className="text-left px-3 py-2">Auth</th>
                    <th className="text-left px-3 py-2">Refresh</th>
                    <th className="text-left px-3 py-2">Last sync</th>
                    <th className="text-left px-3 py-2">Records</th>
                    <th className="text-left px-3 py-2">State</th>
                    <th className="text-right px-3 py-2">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {byCategory[cat].map((c) => {
                    const h = healthBy[c.id] || {};
                    const st = STATE_TONE[h.state] || STATE_TONE.NEVER_RUN;
                    const cost = COST_LABEL[c.cost] || { label: c.cost, tone: "amber" };
                    const canSync = c.sync_supported ?? ["cisa-kev", "nvd", "epss", "ecb-fx"].includes(c.id);
                    return (
                      <tr key={c.id} className="hover:bg-slate-50"
                          data-testid={`oc-free-row-${c.id}`}>
                        <td className="px-3 py-2">
                          <div className="text-[13px] font-medium text-[#101828]">{c.name}</div>
                          <div className="text-[11px] text-[#667085]">{c.provider}</div>
                        </td>
                        <td className="px-3 py-2">
                          <span className={`inline-flex rounded-full px-2 py-0.5 text-[10px] font-semibold ${
                            cost.tone === "mint" ? "bg-[#ECFDF6] text-[#169C70]" :
                            cost.tone === "blue" ? "bg-[#EFF5FF] text-[#246BFD]" :
                            cost.tone === "coral" ? "bg-[#FFF0F2] text-[#C2333F]" :
                                                            "bg-[#FFF8E7] text-[#B25A00]"}`}>
                            {cost.label}
                          </span>
                        </td>
                        <td className="px-3 py-2">
                          <span className="inline-flex rounded-full px-2 py-0.5 text-[10px] font-semibold bg-[#ECFDF6] text-[#169C70]"
                            data-testid={`oc-free-access-${c.id}`}>
                            {(c.access_mode || "PUBLIC_NO_KEY").replaceAll("_", " ")}
                          </span>
                          <div className="text-[10px] font-mono text-[#98A2B3] mt-0.5">{c.auth}</div>
                        </td>
                        <td className="px-3 py-2 text-[11px] text-[#667085]">{c.refresh}</td>
                        <td className="px-3 py-2 text-[11px] text-[#667085]">
                          {h.last_success_at ? new Date(h.last_success_at).toLocaleString() : "—"}
                        </td>
                        <td className="px-3 py-2 text-[12px] tabular-nums text-[#101828]">
                          {(h.record_count ?? 0).toLocaleString()}
                        </td>
                        <td className="px-3 py-2">
                          <span className="inline-flex rounded-full px-2 py-0.5 text-[10px] font-semibold"
                                  style={{ background: st.bg, color: st.fg }}>{st.label}</span>
                          {h.last_error && (
                            <div className="text-[10px] text-[#C2333F] mt-1 flex items-center gap-1">
                              <AlertTriangle className="h-3 w-3" />
                              <span className="truncate max-w-[220px]">{h.last_error}</span>
                            </div>
                          )}
                        </td>
                        <td className="px-3 py-2 text-right">
                          {canSync ? (
                            <Button size="sm" onClick={() => runSync(c.id)}
                                    disabled={syncing === c.id}
                                    className="bg-brand-blue hover:bg-brand-blueh text-white"
                                    data-testid={`oc-free-sync-${c.id}`}>
                              {syncing === c.id
                                ? <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                                : <RefreshCcw className="h-3 w-3 mr-1" />}
                              Sync
                            </Button>
                          ) : (
                            <span className="text-[11px] text-[#98A2B3]" title="Queried live when a record needs it — no bulk sync required">On-demand source · no sync needed</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        );
      })}

      {busy && (
        <div className="text-center text-[12px] text-[#98A2B3] py-4">
          <Loader2 className="h-4 w-4 mx-auto animate-spin" />
        </div>
      )}
    </div>
  );
}
